import { Geolocation } from '@capacitor/geolocation';
import { Capacitor } from '@capacitor/core';

export interface GeolocationCoordinates {
  lat: number;
  lng: number;
  accuracy?: number;
  altitude?: number | null;
  altitudeAccuracy?: number | null;
  heading?: number | null;
  speed?: number | null;
}

/**
 * Check if running on native platform (Android/iOS)
 */
const isNativePlatform = (): boolean => {
  return Capacitor.isNativePlatform();
};

/**
 * Get current location using browser's geolocation API (for web)
 */
const getCurrentLocationWeb = async (
  highAccuracy: boolean = true,
  timeout: number = 10000
): Promise<GeolocationCoordinates> => {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('Geolocation is not supported by your browser'));
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        resolve({
          lat: position.coords.latitude,
          lng: position.coords.longitude,
          accuracy: position.coords.accuracy,
          altitude: position.coords.altitude,
          altitudeAccuracy: position.coords.altitudeAccuracy,
          heading: position.coords.heading,
          speed: position.coords.speed,
        });
      },
      (error) => {
        let errorMessage = 'Failed to get location';
        
        switch (error.code) {
          case error.PERMISSION_DENIED:
            errorMessage = 'Location permission denied. Please enable location access in settings.';
            break;
          case error.POSITION_UNAVAILABLE:
            errorMessage = 'Location information is unavailable.';
            break;
          case error.TIMEOUT:
            errorMessage = 'Location request timed out. Please try again.';
            break;
        }
        
        reject(new Error(errorMessage));
      },
      {
        enableHighAccuracy: highAccuracy,
        timeout: timeout,
        maximumAge: 0,
      }
    );
  });
};

/**
 * Get the current position of the device
 * @param highAccuracy - Whether to use high accuracy mode (default: true)
 * @param timeout - Maximum time to wait for location (default: 10000ms)
 * @returns GeolocationCoordinates object with lat, lng, and optional accuracy
 */
export async function getCurrentLocation(
  highAccuracy: boolean = true,
  timeout: number = 10000
): Promise<GeolocationCoordinates> {
  try {
    // Use web API for browser, Capacitor for native
    if (!isNativePlatform()) {
      return await getCurrentLocationWeb(highAccuracy, timeout);
    }

    // Native platform - use Capacitor Geolocation
    // Request permissions first
    const permissions = await Geolocation.requestPermissions();
    
    if (permissions.location === 'denied') {
      throw new Error('Location permission denied. Please enable location access in settings.');
    }

    // Get current position
    const position = await Geolocation.getCurrentPosition({
      enableHighAccuracy: highAccuracy,
      timeout: timeout,
      maximumAge: 0,
    });

    return {
      lat: position.coords.latitude,
      lng: position.coords.longitude,
      accuracy: position.coords.accuracy,
      altitude: position.coords.altitude,
      altitudeAccuracy: position.coords.altitudeAccuracy,
      heading: position.coords.heading,
      speed: position.coords.speed,
    };
  } catch (error) {
    console.error('Geolocation error:', error);
    
    // Provide more specific error messages
    if (error instanceof Error) {
      if (error.message.includes('denied')) {
        throw new Error('Location permission denied. Please enable location access in settings.');
      } else if (error.message.includes('timeout')) {
        throw new Error('Location request timed out. Please try again.');
      } else if (error.message.includes('unavailable')) {
        throw new Error('Location services are unavailable. Please enable GPS.');
      }
    }
    
    throw new Error('Failed to get location. Please ensure location services are enabled.');
  }
}

/**
 * Check if location permissions are granted
 * @returns Promise<boolean> - True if permissions are granted
 */
export async function checkLocationPermissions(): Promise<boolean> {
  try {
    // On web, we can't check permissions before requesting, so return true
    if (!isNativePlatform()) {
      return true;
    }
    
    const permissions = await Geolocation.checkPermissions();
    return permissions.location === 'granted';
  } catch (error) {
    console.error('Error checking location permissions:', error);
    return false;
  }
}

/**
 * Request location permissions
 * @returns Promise<boolean> - True if permissions are granted
 */
export async function requestLocationPermissions(): Promise<boolean> {
  try {
    // On web, permissions are requested automatically when using geolocation
    if (!isNativePlatform()) {
      return true;
    }
    
    const permissions = await Geolocation.requestPermissions();
    return permissions.location === 'granted';
  } catch (error) {
    console.error('Error requesting location permissions:', error);
    return false;
  }
}

/**
 * Calculate distance between two coordinates using Haversine formula
 * @param lat1 - Latitude of first point
 * @param lon1 - Longitude of first point
 * @param lat2 - Latitude of second point
 * @param lon2 - Longitude of second point
 * @returns Distance in meters
 */
export function calculateDistance(
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number
): number {
  const R = 6371e3; // Earth's radius in meters
  const φ1 = (lat1 * Math.PI) / 180;
  const φ2 = (lat2 * Math.PI) / 180;
  const Δφ = ((lat2 - lat1) * Math.PI) / 180;
  const Δλ = ((lon2 - lon1) * Math.PI) / 180;

  const a =
    Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
    Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return R * c; // Distance in meters
}

/**
 * Watch position changes (useful for tracking)
 * @param callback - Function to call when position changes
 * @param highAccuracy - Whether to use high accuracy mode (default: true)
 * @returns Promise<string> - Watch ID that can be used to clear the watch
 */
export async function watchPosition(
  callback: (position: GeolocationCoordinates) => void,
  highAccuracy: boolean = true
): Promise<string | number> {
  try {
    // Use web API for browser
    if (!isNativePlatform()) {
      if (!navigator.geolocation) {
        throw new Error('Geolocation is not supported by your browser');
      }
      
      const watchId = navigator.geolocation.watchPosition(
        (position) => {
          callback({
            lat: position.coords.latitude,
            lng: position.coords.longitude,
            accuracy: position.coords.accuracy,
            altitude: position.coords.altitude,
            altitudeAccuracy: position.coords.altitudeAccuracy,
            heading: position.coords.heading,
            speed: position.coords.speed,
          });
        },
        (error) => {
          console.error('Watch position error:', error);
        },
        {
          enableHighAccuracy: highAccuracy,
          timeout: 10000,
          maximumAge: 0,
        }
      );
      
      return watchId;
    }
    
    // Native platform - use Capacitor
    // Request permissions first
    const hasPermission = await requestLocationPermissions();
    
    if (!hasPermission) {
      throw new Error('Location permission denied');
    }

    const watchId = await Geolocation.watchPosition(
      {
        enableHighAccuracy: highAccuracy,
        timeout: 10000,
        maximumAge: 0,
      },
      (position, err) => {
        if (err) {
          console.error('Watch position error:', err);
          return;
        }
        
        if (position) {
          callback({
            lat: position.coords.latitude,
            lng: position.coords.longitude,
            accuracy: position.coords.accuracy,
            altitude: position.coords.altitude,
            altitudeAccuracy: position.coords.altitudeAccuracy,
            heading: position.coords.heading,
            speed: position.coords.speed,
          });
        }
      }
    );

    return watchId;
  } catch (error) {
    console.error('Error watching position:', error);
    throw error;
  }
}

/**
 * Clear position watch
 * @param watchId - Watch ID returned from watchPosition
 */
export async function clearWatch(watchId: string | number): Promise<void> {
  try {
    // Use web API for browser
    if (!isNativePlatform()) {
      if (typeof watchId === 'number') {
        navigator.geolocation.clearWatch(watchId);
      }
      return;
    }
    
    // Native platform - use Capacitor
    if (typeof watchId === 'string') {
      await Geolocation.clearWatch({ id: watchId });
    }
  } catch (error) {
    console.error('Error clearing watch:', error);
  }
}
